import { Plus, Copy } from "lucide-react";
import { useEffect, useState } from "react";
import { UpgradePrompt } from "@/components/billing/UpgradePrompt";
import {
  SubscriptionEssentialsForm,
  SubscriptionBillingForm,
  SubscriptionStatusForm,
  SubscriptionPreferencesForm,
  SubscriptionNotesForm,
  StartDateValidationAlert,
} from "@/components/subscription-form";
import { Accordion } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { useDialogState } from "@/hooks/useDialogState";
import { useSubscriptionFormSubmission } from "@/hooks/useSubscriptionFormSubmission";
import { useSubscriptions } from "@/hooks/useSubscriptions";
import { useTagInitialization } from "@/hooks/useTagInitialization";
import { FREE_PLAN_SUBSCRIPTION_LIMIT, useUserPlan } from "@/hooks/useUserPlan";
import { createInitialFormData, updateFormData } from "@/lib/formDataHelpers";
import { type Subscription } from "@/types/subscription";
import { type DebugCopyOptions } from "@/lib/debugHelpers";

interface AddSubscriptionDialogProps {
  subscription?: Subscription;
  onComplete?: () => void;
}

export const AddSubscriptionDialog = ({
  subscription,
  onComplete,
}: AddSubscriptionDialogProps) => {
  const { isPro } = useUserPlan();
  const { subscriptions } = useSubscriptions();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [copyOptionsOpen, setCopyOptionsOpen] = useState(false);
  const [copyOptions, setCopyOptions] = useState<DebugCopyOptions>({
    payments: true,
    lifecycleEvents: true,
    subscriptionPeriods: true,
    tags: true,
  });

  // Count only non-cancelled subscriptions against the free plan limit
  const activeSubscriptionCount = subscriptions.filter(
    (s) => s.status !== "cancelled",
  ).length;
  const isAtFreeLimit =
    !isPro && activeSubscriptionCount >= FREE_PLAN_SUBSCRIPTION_LIMIT;

  // Track the current subscription being edited (initial prop or created)
  const [currentSubscription, setCurrentSubscription] = useState<
    Subscription | undefined
  >(subscription);

  // Track if this dialog was originally opened for editing (has initial subscription)
  // This prevents switching to edit mode after creation for add-only dialogs
  const wasOriginallyForEditing = !!subscription;

  // isEditing should be true if:
  // 1. We have a current subscription AND it was originally for editing, OR
  // 2. We have an initial subscription (classic edit mode)
  const isEditing = wasOriginallyForEditing && !!currentSubscription;

  // Use shared dialog state hook
  const { open, setOpen } = useDialogState({ initialOpen: isEditing });

  // Synchronize internal editing state if a subscription prop appears after initial mount.
  // This covers flows where the dialog was first rendered for "add" (no subscription)
  // and later receives a subscription to edit. Without this, currentSubscription stayed
  // undefined and the component incorrectly remained in add mode, triggering create logic.
  useEffect(() => {
    if (subscription) {
      setCurrentSubscription(subscription);
      setFormData(createInitialFormData(subscription));
      // Ensure dialog opens automatically when a subscription is provided
      setOpen(true);
    }
  }, [subscription, setOpen]);

  // Use tag initialization hook
  const { tagIds, setTagIds } = useTagInitialization({
    subscription: currentSubscription,
  });

  // Initialize form data
  const [formData, setFormData] = useState(
    createInitialFormData(currentSubscription),
  );
  const [startDateValidation, setStartDateValidation] = useState<any>(null);

  // Wrapper function for updateFormData to match expected signature
  const updateFormDataWrapper = (key: string, value: any) => {
    setFormData((prev) =>
      updateFormData(prev, key as keyof typeof prev, value),
    );
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen && !currentSubscription) {
      setFormData(createInitialFormData());
      setTagIds([]);
    }
    setOpen(newOpen);
    if (!newOpen && isEditing) {
      onComplete?.();
    }
  };

  // Use the submission hook
  const {
    submitForm,
    handleDebugCopy,
    isLoading,
    mutationsReady,
    tagIds: submissionTagIds,
    setTagIds: setTagIdsForSubmission,
  } = useSubscriptionFormSubmission({
    subscription: currentSubscription,
    formData,
    tagIds,
    setFormData,
    setStartDateValidation,
    onClose: () => setOpen(false),
    onComplete,
    startDateValidation,
    isDirty: true, // Form is considered dirty when dialog is open for editing
  });

  // Keep the submission hook's internal tagIdsState in sync with the
  // tagIds managed by useTagInitialization so newly created/selected tags
  // are persisted on create.
  useEffect(() => {
    if (
      typeof setTagIdsForSubmission === "function" &&
      Array.isArray(submissionTagIds)
    ) {
      const same =
        submissionTagIds.length === tagIds.length &&
        submissionTagIds.every((v, i) => v === tagIds[i]);
      if (!same) setTagIdsForSubmission(tagIds);
    }
  }, [tagIds, setTagIdsForSubmission, submissionTagIds]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const originalEditing = !!subscription; // True if component was opened with a subscription
    const result = await submitForm();
    if (result && !originalEditing) {
      // After creation, only clear the name field
      // All other fields (amount, currency, frequency, tags) persist for quick multi-add
      setFormData((prev) => ({ ...prev, name: "" }));
      // Don't modify currentSubscription - keep dialog in add mode
      // Don't modify tagIds - they persist and remain editable
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {!isEditing && (
        <>
          {isAtFreeLimit ? (
            <Button
              className="btn-premium shadow-strong fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full"
              onClick={() => setShowUpgradePrompt(true)}
            >
              <Plus className="h-6 w-6" />
            </Button>
          ) : (
            <DialogTrigger asChild>
              <Button className="btn-premium shadow-strong fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full">
                <Plus className="h-6 w-6" />
              </Button>
            </DialogTrigger>
          )}
          <UpgradePrompt
            open={showUpgradePrompt}
            onOpenChange={setShowUpgradePrompt}
          />
        </>
      )}

      <DialogContent className="glass-card max-h-[90vh] overflow-y-auto overflow-x-visible sm:max-w-[700px]">
        <DialogHeader>
          <div className="flex items-center justify-between gap-2">
            <DialogTitle className="text-xl font-bold">
              {isEditing ? "Edit Subscription" : "Add New Subscription"}
            </DialogTitle>
            {isEditing && currentSubscription?.id && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setCopyOptionsOpen(true)}
                title="Copy JSON"
              >
                <Copy className="h-4 w-4" />
              </Button>
            )}
          </div>
          <DialogDescription>
            {isEditing
              ? "Edit subscription details such as amount, billing cycle, and lifecycle settings."
              : "Create a new subscription record with billing, status, and preferences."}
          </DialogDescription>
        </DialogHeader>
        <Dialog open={copyOptionsOpen} onOpenChange={setCopyOptionsOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Copy subscription JSON</DialogTitle>
              <DialogDescription>
                Choose the related data to include. The subscription is always included.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {([
                ["payments", "Payments"],
                ["lifecycleEvents", "Lifecycle events"],
                ["subscriptionPeriods", "Subscription periods"],
                ["tags", "Tags"],
              ] as const).map(([key, label]) => (
                <label key={key} className="flex items-center gap-3">
                  <Checkbox
                    checked={copyOptions[key]}
                    onCheckedChange={(checked) =>
                      setCopyOptions((previous) => ({
                        ...previous,
                        [key]: checked === true,
                      }))
                    }
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
            <Button
              type="button"
              onClick={async () => {
                await handleDebugCopy(copyOptions);
                setCopyOptionsOpen(false);
              }}
            >
              Copy JSON
            </Button>
          </DialogContent>
        </Dialog>

        <form onSubmit={handleSubmit} className="space-y-5">
          <Accordion
            type="multiple"
            defaultValue={["essentials"]}
            className="space-y-2"
          >
            <SubscriptionEssentialsForm
              formData={formData}
              updateFormData={updateFormDataWrapper}
              tagIds={tagIds}
              setTagIds={setTagIds}
              subscription={subscription}
              isEditing={isEditing}
              startDateValidation={startDateValidation}
              setStartDateValidation={setStartDateValidation}
              onStartDateValidationAlert={
                <StartDateValidationAlert
                  startDateValidation={startDateValidation}
                  isEditing={isEditing}
                />
              }
            />

            <SubscriptionBillingForm
              formData={formData}
              updateFormData={updateFormDataWrapper}
            />

            <SubscriptionStatusForm
              formData={formData}
              updateFormData={updateFormDataWrapper}
            />

            <SubscriptionPreferencesForm
              formData={formData}
              updateFormData={updateFormDataWrapper}
            />

            <SubscriptionNotesForm
              formData={formData}
              updateFormData={updateFormDataWrapper}
            />
          </Accordion>

          <Button
            type="submit"
            className="btn-premium w-full"
            disabled={isLoading || !mutationsReady}
          >
            {isLoading
              ? "Saving..."
              : !mutationsReady
                ? "Initializing..."
                : isEditing
                  ? "Update Subscription"
                  : "Add Subscription"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
